import { GsFormField, GsFormIfArg } from '../../../components';

/** Options to customise {@link teaserBadgeSchemaFields}. */
export interface TeaserBadgeSchemaOptions {
  /**
   * Translation root used to build the label keys.
   * The helper appends `.showItemBadge`, `.showDiscountBadge`, `.showAvailableStatusBadge`,
   * `.showArticleTeaserBadge` and `.showItemTypeBadge` to this root.
   * @default 'components.searchGallery.schema'
   */
  translationRoot?: string;
  /**
   * Grouping name applied to every field.
   * @default `${translationRoot}.itemTeaserOptions`
   */
  groupingName?: string;
  /**
   * Whether to include the `showItemTypeBadge` field.
   * Report-detail for example does not expose this toggle.
   * @default true
   */
  includeItemTypeBadge?: boolean;
  /**
   * Additional `if` arguments that are prepended to each field's display condition.
   * Useful when the host schema only wants these fields visible for specific view-templates
   * (see container-gallery).
   */
  additionalIfArgs?: GsFormIfArg[];
}

/**
 * Generates the form fields for the item-teaser badge toggles that are repeated
 * across several page components. The dependent badges (discount, available-status,
 * article-teaser, item-type) are automatically bound to the `showItemBadge` master
 * toggle.
 */
export function teaserBadgeSchemaFields(options: TeaserBadgeSchemaOptions = {}): GsFormField<never>[] {
  const translationRoot = options.translationRoot ?? 'components.searchGallery.schema';
  const groupingName = options.groupingName ?? `${translationRoot}.itemTeaserOptions`;
  const includeItemTypeBadge = options.includeItemTypeBadge ?? true;
  const extraArgs = options.additionalIfArgs ?? [];

  const dependentArgs: GsFormIfArg[] = [
    ...extraArgs,
    { formControlName: 'showItemBadge', value: true }
  ];

  const fields: GsFormField<never>[] = [
    {
      formControlName: 'showItemBadge',
      label: `${translationRoot}.showItemBadge`,
      type: 'checkbox',
      default: true,
      groupingName,
      ...(extraArgs.length ? { if: { args: [...extraArgs] } } : {})
    },
    {
      formControlName: 'showDiscountBadge',
      label: `${translationRoot}.showDiscountBadge`,
      type: 'checkbox',
      default: false,
      groupingName,
      if: { args: dependentArgs }
    },
    {
      formControlName: 'showAvailableStatusBadge',
      label: `${translationRoot}.showAvailableStatusBadge`,
      type: 'checkbox',
      default: true,
      groupingName,
      if: { args: dependentArgs }
    },
    {
      formControlName: 'showArticleTeaserBadge',
      label: `${translationRoot}.showArticleTeaserBadge`,
      type: 'checkbox',
      default: true,
      groupingName,
      if: { args: dependentArgs }
    }
  ];

  if (includeItemTypeBadge) {
    fields.push({
      formControlName: 'showItemTypeBadge',
      label: `${translationRoot}.showItemTypeBadge`,
      type: 'checkbox',
      default: false,
      groupingName,
      if: { args: dependentArgs }
    });
  }

  return fields;
}
